import type { DeviceSync, SyncSegment } from "@workspace/api-client-react";
import {
  activeSegmentAt,
  driftSec,
  positionInSegment,
  resolveSyncGroupId as coreResolveSyncGroupId,
  syncShuffleBase,
} from "@workspace/player-core";

export {
  activeSegmentAt,
  computeDeterministicSeed,
  driftSec,
  positionInSegment,
  resolveSyncGroupId,
} from "@workspace/player-core";

/** Sync group id for a device sync payload (settings override or location). */
export function syncGroupIdFromSync(sync: DeviceSync | null): string | null {
  if (!sync?.device) return null;
  return coreResolveSyncGroupId(
    sync.device.settings,
    sync.device.locationId ?? null,
  );
}

/** Shuffle base seed — prefers server-provided deterministicSeed. */
export function syncShuffleSeed(sync: DeviceSync | null): string {
  return syncShuffleBase(sync);
}

export type TimelineChannel = SyncSegment["channelType"];

/** Segments for one channel from the server timeline (per-channel capped). */
export function channelTimeline(
  sync: DeviceSync | null,
  channelType: TimelineChannel,
): SyncSegment[] {
  return (sync?.segmentTimeline ?? []).filter(
    (s) => s.channelType === channelType,
  );
}

/** Music-channel segments from the server timeline. */
export function musicTimeline(sync: DeviceSync | null): SyncSegment[] {
  return channelTimeline(sync, "music");
}

/**
 * Index in the playlist rotation for a timeline segment. Prefers the
 * server-issued rotationIndex — weighted rotations repeat itemIds, so the
 * first indexOf occurrence can be the wrong phase of the cycle — trusting it
 * only when it points at the segment's item in the local rotation (a stale
 * local array must not be indexed blindly). Payloads without the field fall
 * back to the first itemId occurrence; -1 means the item is not in the
 * rotation, and callers must treat that as no-alignment, never index 0.
 */
export function rotationIndexForTimelineItem(
  rotationItemIds: readonly string[],
  segment: Pick<SyncSegment, "itemId" | "rotationIndex">,
): number {
  const serverIndex = segment.rotationIndex;
  if (
    serverIndex != null &&
    Number.isInteger(serverIndex) &&
    rotationItemIds[serverIndex] === segment.itemId
  ) {
    return serverIndex;
  }
  return rotationItemIds.indexOf(segment.itemId);
}

/** Whether the sync payload includes multi-room timeline guidance. */
export function hasSyncTimeline(
  sync: DeviceSync | null,
  channelType: TimelineChannel = "music",
): boolean {
  return channelTimeline(sync, channelType).length > 0;
}

export type TimelineAlignment = {
  segment: SyncSegment;
  rotationIndex: number;
  seekSec: number;
};

/**
 * Resolve where a channel's playback should be when following the server
 * timeline. Returns null when no active segment or the item is missing from
 * the rotation.
 */
export function alignChannelToTimeline(
  sync: DeviceSync | null,
  channelType: TimelineChannel,
  rotationItemIds: readonly string[],
  now: Date = new Date(),
): TimelineAlignment | null {
  const timeline = channelTimeline(sync, channelType);
  const active = activeSegmentAt(timeline, now);
  if (!active) return null;
  // Re-read through the wire-typed array: activeSegmentAt narrows to the
  // player-core segment shape, which drops the optional rotationIndex.
  const segment = timeline[active.index];
  const idx = rotationIndexForTimelineItem(rotationItemIds, segment);
  if (idx < 0) return null;
  return {
    segment,
    rotationIndex: idx,
    seekSec: positionInSegment(segment, now),
  };
}

/**
 * Resolve where music playback should be when following the server timeline.
 * Returns null when no active segment or item is missing from rotation.
 */
export function alignMusicToTimeline(
  sync: DeviceSync | null,
  rotationItemIds: readonly string[],
  now: Date = new Date(),
): TimelineAlignment | null {
  return alignChannelToTimeline(sync, "music", rotationItemIds, now);
}

/** Drift of actual position vs timeline for a channel's active segment. */
export function channelDriftSec(
  sync: DeviceSync | null,
  channelType: TimelineChannel,
  itemId: string | null | undefined,
  positionSec: number,
  now: Date = new Date(),
): number | null {
  if (!itemId) return null;
  const timeline = channelTimeline(sync, channelType);
  const active = activeSegmentAt(timeline, now);
  if (!active || active.segment.itemId !== itemId) return null;
  return driftSec(active.segment, positionSec, now);
}

/** Drift of actual position vs timeline for the active music segment. */
export function musicDriftSec(
  sync: DeviceSync | null,
  itemId: string | null | undefined,
  positionSec: number,
  now: Date = new Date(),
): number | null {
  return channelDriftSec(sync, "music", itemId, positionSec, now);
}
